import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LifeBuoy, BookOpen, Music, Sparkles, Baby, ArrowRight, Phone, Heart, Quote } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionTitle } from "@/components/Bits";
import { useEmergency } from "@/components/EmergencyProvider";
import { IMG } from "@/lib/images";
import { AFFIRMATIONS, STORIES } from "@/lib/content";
import { WeldSparks } from "@/components/WeldSparks";
import { STORE_VISIBLE } from "@/lib/flags";

const PILLARS = [
  { to: "/resources", icon: LifeBuoy, title: "Find Resources", text: "A curated directory of trusted crisis lines and specialized support, ready the moment you need it.", color: "#DC2626" },
  { to: "/stories", icon: BookOpen, title: "Stories of Survival", text: "First-person journeys from darkness toward light — proof that this chapter is not the whole story.", color: "#6366F1" },
  { to: "/sanctuary", icon: Music, title: "Calming Sanctuary", text: "Soothing generated soundscapes and tranquil visuals to steady your breath and quiet the noise.", color: "#0E7490" },
  { to: "/super-kids", icon: Baby, title: "Super Kids", text: "Downloadable bookmarks, feelings tools and brave affirmations crafted for our youngest hearts.", color: "#B45309" },
];

const STATS = [
  { value: "24/7", label: "Support lines surfaced on every page" },
  { value: "70+", label: "International helplines linked" },
  { value: "100%", label: "Free crisis resources, always" },
  { value: "∞", label: "Reasons your story matters" },
];

export default function Home() {
  const { open } = useEmergency();
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % AFFIRMATIONS.length), 4200);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative bg-bsn-midnight text-bsn-parchment overflow-hidden">
        <img src={IMG.heroDawn} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-bsn-midnight via-bsn-midnight/90 to-bsn-navy/85" />
        <div className="relative max-w-7xl mx-auto px-4 py-24 sm:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal><p className="eyebrow text-bsn-gold mb-5">Rebuilding Hope · Anchoring Tomorrow</p></Reveal>
            <Reveal delay={90}>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] tracking-tight text-balance">
                Your story is still being written — <span className="gold-text">and it is worth staying for.</span>
              </h1>
            </Reveal>
            <Reveal delay={180}>
              <p className="mt-7 text-lg text-bsn-ice/80 leading-relaxed max-w-xl">
                Bright Stories Network is a refined, judgment-free sanctuary for finding support,
                rebuilding hope, and reaching trusted resources — including 988 and The Trevor Project.
              </p>
            </Reveal>
            <Reveal delay={260}>
              <div className="mt-9 flex flex-wrap gap-3">
                <button onClick={open} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full btn-gold font-semibold transition-all" data-testid="hero-get-help-btn">
                  <LifeBuoy className="w-5 h-5" /> Get Help Now
                </button>
                <Link to="/sanctuary" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-bsn-gold/50 text-bsn-parchment font-semibold hover:bg-bsn-gold/10 transition-colors" data-testid="hero-sanctuary-btn">
                  <Music className="w-5 h-5" /> Enter the Sanctuary
                </Link>
              </div>
            </Reveal>
            <Reveal delay={340}>
              <a href="tel:988" className="mt-7 inline-flex items-center gap-2 text-sm text-bsn-ice/70 hover:text-bsn-gold transition-colors">
                <Phone className="w-4 h-4" /> In crisis right now? Call or text <strong className="text-bsn-champagne">988</strong>
              </a>
            </Reveal>
          </div>

          <div className="lg:col-span-5 flex justify-center">
            <Reveal delay={200}>
              <div className="float-soft relative">
                <img src={IMG.seal} alt="Bright Stories Network embossed seal" className="w-64 sm:w-80 rounded-2xl shadow-2xl ring-1 ring-bsn-gold/40" data-testid="hero-seal-image" />
                <WeldSparks />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Emergency hub strip */}
      <section className="bg-bsn-navy text-bsn-parchment border-y border-bsn-gold/25">
        <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-serif text-xl text-center sm:text-left">Immediate, confidential help is always within reach.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="tel:988" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-gold text-sm font-semibold" data-testid="hub-call-988">
              <Phone className="w-4 h-4" /> Call or Text 988
            </a>
            <a href="tel:18664887386" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-bsn-parchment text-bsn-midnight text-sm font-semibold hover:bg-bsn-gold transition-colors" data-testid="hub-call-trevor">
              The Trevor Project
            </a>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-7xl mx-auto px-4 py-20 sm:py-24">
        <SectionTitle eyebrow="Four paths to steady ground" title="However you arrived here, there is a place for you" subtitle="Explore the parts of Bright Stories Network that meet you where you are today." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PILLARS.map((p, i) => (
            <Reveal key={p.to} delay={i * 90}>
              <Link to={p.to} className="group block h-full bg-white rounded-2xl p-7 card-tactile transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(11,25,44,0.14)]" data-testid={`pillar-${p.to.replace("/", "")}`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${p.color}15` }}>
                  <p.icon className="w-6 h-6" style={{ color: p.color }} />
                </div>
                <h3 className="font-serif text-xl font-semibold text-bsn-midnight mb-2">{p.title}</h3>
                <p className="text-sm text-bsn-sapphire/80 leading-relaxed">{p.text}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-bsn-bronze group-hover:gap-2 transition-all">
                  Explore <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Live affirmation */}
      <section className="relative bg-bsn-midnight text-bsn-parchment overflow-hidden">
        <img src={IMG.lakeDock} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-bsn-midnight/80" />
        <div className="relative max-w-4xl mx-auto px-4 py-24 text-center">
          <Sparkles className="w-8 h-8 text-bsn-gold mx-auto mb-6" />
          <p className="eyebrow text-bsn-gold mb-6">Today's gentle anchor</p>
          <p key={idx} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-snug gold-text min-h-[3em] flex items-center justify-center text-balance reveal is-visible" data-testid="home-affirmation">
            “{AFFIRMATIONS[idx]}”
          </p>
          <Link to="/affirmations" className="mt-10 inline-flex items-center gap-2 px-7 py-3.5 rounded-full btn-gold font-semibold transition-all" data-testid="home-affirmations-cta">
            Open the Daily Anchor Deck <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Stories teaser */}
      <section className="max-w-7xl mx-auto px-4 py-20 sm:py-24">
        <SectionTitle eyebrow="Survivor Stories" title="Light, told in first person" subtitle="Real voices who walked through the dark and chose to stay. Their words may be the ones you need today." />
        <div className="grid md:grid-cols-3 gap-6">
          {STORIES.slice(0, 3).map((s, i) => (
            <Reveal key={s.id} delay={i * 90}>
              <div className="h-full bg-white rounded-2xl p-7 card-tactile flex flex-col">
                <Quote className="w-8 h-8 text-bsn-gold/60 mb-4" />
                <p className="font-serif text-xl text-bsn-midnight leading-snug mb-4">“{s.quote}”</p>
                <p className="text-sm text-bsn-sapphire/80 leading-relaxed flex-1">{s.excerpt}</p>
                <div className="mt-5 pt-4 border-t border-bsn-gold/20 flex items-center justify-between">
                  <span className="font-semibold text-bsn-midnight text-sm">{s.name}</span>
                  <span className="text-[11px] font-mono uppercase tracking-wider text-bsn-bronze">{s.tag}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/stories" className="inline-flex items-center gap-2 font-semibold text-bsn-bronze hover:gap-3 transition-all" data-testid="home-stories-cta">
            Read more stories of survival <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Impact stats */}
      <section className="bg-bsn-canvas border-y border-bsn-gold/20">
        <div className="max-w-7xl mx-auto px-4 py-16 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 80}>
              <p className="font-display text-4xl sm:text-5xl font-bold gold-text">{s.value}</p>
              <p className="mt-2 text-sm text-bsn-sapphire/80 leading-snug">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Stay song announcement */}
      <section className="relative bg-bsn-navy text-bsn-parchment overflow-hidden border-y border-bsn-gold/25">
        <div className="relative max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-[auto_1fr] gap-8 items-center">
          <Reveal>
            <div className="relative mx-auto">
              <img src={IMG.stayCover} alt="STAY single cover" className="w-48 sm:w-56 rounded-2xl shadow-2xl ring-1 ring-bsn-gold/40" />
              <span className="absolute -top-2 -right-2 bg-bsn-gold text-bsn-midnight text-[10px] font-bold px-2.5 py-1 rounded-full">NEW SINGLE</span>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div>
              <p className="eyebrow text-bsn-gold mb-3 flex items-center gap-2"><Music className="w-4 h-4" /> Coming January 2027</p>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-balance">A new song written for suicide prevention — <span className="gold-text">"Stay"</span></h2>
              <p className="mt-4 text-bsn-ice/80 leading-relaxed max-w-xl">
                A soaring anthem that says the one word that can change everything. Be the first to hear it the moment it releases.
              </p>
              <Link to="/stay" className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full btn-gold font-semibold transition-all" data-testid="home-stay-cta">
                <Music className="w-5 h-5" /> Hear the Story of "Stay"
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Donate band */}
      {STORE_VISIBLE && <section className="relative bg-bsn-midnight text-bsn-parchment overflow-hidden">
        <div className="relative max-w-5xl mx-auto px-4 py-20 text-center">
          <Heart className="w-9 h-9 text-bsn-gold mx-auto mb-5" />
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-balance">Help keep the light on for someone tonight</h2>
          <p className="mt-5 text-bsn-ice/80 max-w-2xl mx-auto leading-relaxed">
            Every gift keeps crisis resources free, funds survivor recordings, and places brave bookmarks in children's hands.
          </p>
          <Link to="/donations" className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full btn-gold font-semibold text-lg transition-all" data-testid="home-donate-cta">
            <Heart className="w-5 h-5" /> Become a Beacon of Hope
          </Link>
        </div>
      </section>}
    </div>
  );
}
